// Candidate tables: one per described tabular file of the project. A curator renames
// tables, excludes them from the build and picks the primary key; saved with PUT .../dbdesign.
// Below them, the hand-defined tables no file supplies (HandTablesPanel, built empty).
import { useEffect, useState } from "react";
import { Badge, Btn, Input, Select, Toggle } from "../../components/Shared";
import { Empty } from "../../components/Feedback";
import HandTablesPanel from "./HandTablesPanel";

const PK_SOURCE = { stats: "guessed from column statistics", inferred: "inferred from the data", manual: "set by hand" };
const cell = { padding: "8px 10px", borderTop: "1px solid #e5e7eb", verticalAlign: "middle", fontSize: 14 };

function asEdits(tables) {
  const out = {};
  tables.forEach(t => {
    out[t.data_file_id] = { name: t.name, include: t.include, primary_key: t.primary_key || "" };
  });
  return out;
}

function FileTables({ tables, canManage, busy, onSave }) {
  const [edits, setEdits] = useState(() => asEdits(tables));
  useEffect(() => { setEdits(asEdits(tables)); }, [tables]);
  if (tables.length === 0) {
    return <Empty>No tabular file with a column dictionary in this project yet. Upload CSV, TSV or XLSX files to a dataset first.</Empty>;
  }
  const set = (id, k, v) => setEdits(e => ({ ...e, [id]: { ...e[id], [k]: v } }));
  const changed = tables.filter(t => {
    const e = edits[t.data_file_id];
    return e && (e.name !== t.name || e.include !== t.include || (e.primary_key || null) !== (t.primary_key || null));
  });

  const save = () => onSave(changed.map(t => {
    const e = edits[t.data_file_id];
    return { data_file_id: t.data_file_id, name: e.name.trim(), include: e.include, primary_key: e.primary_key || null };
  }));

  return (
    <div>
      <div data-table-scroll style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ textAlign: "left", fontSize: 12, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              <th style={{ padding: "6px 10px" }}>Table</th>
              <th style={{ padding: "6px 10px" }}>Source</th>
              <th style={{ padding: "6px 10px" }}>Rows</th>
              <th style={{ padding: "6px 10px" }}>Primary key</th>
              <th style={{ padding: "6px 10px" }}>Build</th>
            </tr>
          </thead>
          <tbody>
            {tables.map(t => {
              const e = edits[t.data_file_id] || { name: t.name, include: t.include, primary_key: t.primary_key || "" };
              return (
                <tr key={t.data_file_id} style={{ opacity: e.include ? 1 : 0.6 }}>
                  <td style={cell}>
                    {canManage ? (
                      <Input aria-label={`Table name for ${t.filename}`} value={e.name}
                             onChange={ev => set(t.data_file_id, "name", ev.target.value)}
                             style={{ fontFamily: "'JetBrains Mono', monospace", padding: "6px 10px", minWidth: 160 }} />
                    ) : <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{t.name}</span>}
                  </td>
                  <td style={cell}>
                    <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                      <Badge color="blue">{t.study_name || t.study}</Badge>
                      <span>{t.dataset_name}</span>
                    </div>
                    <div style={{ fontSize: 12, color: "#6b7280" }}>{t.filename} · {t.fields.length} columns{t.owner && <> · owner {t.owner}</>}</div>
                  </td>
                  <td style={{ ...cell, fontFamily: "'JetBrains Mono', monospace" }}>{t.row_count ?? "–"}</td>
                  <td style={cell}>
                    {canManage ? (
                      <Select aria-label={`Primary key of ${t.name}`} options={t.fields.map(f => f.name)}
                              value={e.primary_key} onChange={ev => set(t.data_file_id, "primary_key", ev.target.value)} />
                    ) : <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{t.primary_key || "–"}</span>}
                    {t.primary_key && t.pk_source && (
                      <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>{PK_SOURCE[t.pk_source]}</div>
                    )}
                  </td>
                  <td style={cell}>
                    {canManage
                      ? <Toggle checked={e.include} label={`Include ${t.name} in the build`}
                                onChange={v => set(t.data_file_id, "include", v)} />
                      : <Badge color={t.include ? "green" : "gray"}>{t.include ? "included" : "excluded"}</Badge>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {canManage && (
        <div style={{ marginTop: 14, display: "flex", gap: 10, alignItems: "center" }}>
          <Btn onClick={save} disabled={busy || changed.length === 0}>Save tables</Btn>
          {changed.length > 0 && (
            <span style={{ fontSize: 13, color: "#6b7280" }}>
              {changed.length} unsaved change{changed.length === 1 ? "" : "s"}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default function TablesPanel({ tables, canManage, busy, onSave, ws, onHand }) {
  return (
    <>
      <FileTables tables={tables} canManage={canManage} busy={busy} onSave={onSave} />
      {ws && onHand && <HandTablesPanel ws={ws} canManage={canManage} busy={busy} onHand={onHand} />}
    </>
  );
}
